import { Injectable } from '@angular/core';
import { AssetVersionDto } from '@nusign/picflow-backend-types';
import { Rect } from '../classes/rect.class';
import { baseZoom } from '../consts';
import { devicePixelRatio } from '../lib/device-pixel-ratio';
import { isMetaKey } from '../lib/is-meta-key';
import { isTrackpad } from '../lib/is-trackpad';
import { normalizeScrollEvent } from '../lib/normalize-scrol';

@Injectable({
  providedIn: 'root',
})
export class AssetDisplayService {
  private scaleSteps = [1, 2, 5, 10, 20, 50, 100, 200, 400, 800, 1600];

  getButtonFit(version: AssetVersionDto, container: Rect, zoom: number, scale: number): 'width' | 'height' {
    if (!version.data) return 'width';
    const assetAspect = version.data.width / version.data.height;
    const aspect = container.width / container.height;
    if (aspect < assetAspect) {
      if (scale === zoom) return 'height';
      return 'width';
    } else {
      if (scale === zoom) return 'width';
      return 'height';
    }
  }

  public getBoundedContainerSize(version: AssetVersionDto, container: Rect) {
    if (!version.data) return { width: container.width, height: container.height };
    const containerAspect = container.width / container.height;
    const aspect = version.data.width / version.data.height;
    const vertical = aspect < containerAspect;
    return {
      width: vertical ? container.height * aspect : container.width,
      height: vertical ? container.height : container.width / aspect,
    };
  }

  getScaledBounds(version: AssetVersionDto, container: Rect, offset: Rect, scale: number) {
    const boundedContainer = this.getBoundedContainerSize(version, container);

    const scaledContainer = {
      width: boundedContainer.width * scale,
      height: boundedContainer.height * scale,
    };

    const centerOffset = [offset.x * scale, offset.y * scale];

    let w = scaledContainer.width;
    if (w > container.width) w = container.width;
    const halfXSpace = (scaledContainer.width - w) / 2;

    let extraOffset = (scale - 1) * 500;
    if (extraOffset < 0) extraOffset = 0;
    if (extraOffset > 200) extraOffset = 200;
    if (extraOffset > container.width * 0.1) extraOffset = container.width * 0.1;

    const left = halfXSpace - centerOffset[0] + extraOffset;
    const right = halfXSpace + centerOffset[0] + extraOffset;
    let h = scaledContainer.height;
    if (h > container.height) h = container.height;
    const halfYSpace = (scaledContainer.height - h) / 2;
    const top = halfYSpace - centerOffset[1] + extraOffset;
    const bottom = halfYSpace + centerOffset[1] + extraOffset;

    return { left, right, top, bottom, extraOffset, scaledContainer };
  }

  public dirToScale(version: AssetVersionDto, dir: string, container: Rect, scale: number, offsetScale: number) {
    const aspect = container.width / container.height;
    const currentScale = Math.round(offsetScale * 100 * scale * devicePixelRatio());
    if (!version.data) return this.percentToScale(100, offsetScale);
    const assetAspect = version.data.width / version.data.height;

    if (dir === 'fit') dir = aspect < assetAspect ? 'width' : 'height';

    if (dir === 'height' || dir === 'width') {
      let val = 1;
      if (dir === 'width') {
        const widthPercent = (100 / version.data.width) * container.width * devicePixelRatio();
        val = this.percentToScale(widthPercent, offsetScale);
      }
      if (dir === 'height') {
        const heightPercent = (100 / version.data.height) * container.height * devicePixelRatio();
        val = this.percentToScale(heightPercent, offsetScale);
      }
      return val * this.getBaseZoom(offsetScale, 1, true);
    }
    if (dir === 'full') {
      if (currentScale === 100) return this.dirToScale(version, 'fit', container, scale, offsetScale);
      return this.percentToScale(100, offsetScale);
    }
    if (dir === 'in') return this.percentToScale(this.getNextScaleStep('in', offsetScale, scale), offsetScale);
    if (dir === 'out') return this.percentToScale(this.getNextScaleStep('out', offsetScale, scale), offsetScale);
  }

  private getNextScaleStep(dir: 'out' | 'in', offsetScale: number, scale: number) {
    const currentScale = Math.round(offsetScale * 100 * scale * devicePixelRatio());
    const scaleSteps = this.scaleSteps.slice();
    if (dir === 'out') scaleSteps.reverse();

    let _scale: number;
    if (dir === 'in') _scale = scaleSteps.find(scale => scale > currentScale);
    else _scale = scaleSteps.find(scale => scale < currentScale);
    if (!_scale) return scaleSteps[scaleSteps.length - 1];
    return _scale;
  }

  public percentToScale(percent: number, offsetScale: number) {
    const val = ((offsetScale / (offsetScale * offsetScale)) * percent) / 100 / devicePixelRatio();
    return Math.round(val * 10000) / 10000;
  }

  public getBaseZoom(offsetScale: number, scale: number, ignoreMax = false) {
    const percent = Math.round(offsetScale * 100 * scale * devicePixelRatio());

    // If an asset is small enough that zoomed to 100%
    if (percent > 100 && !ignoreMax) {
      const scale = 100 / percent;
      return baseZoom * scale;
    }

    return baseZoom;
  }

  public clipScale(scale: number, offsetScale: number) {
    const percent = Math.round(offsetScale * 100 * scale * devicePixelRatio());
    const biggest = 1600;
    const smallest = Math.round(offsetScale * 100 * this.getBaseZoom(offsetScale, 1));

    if (offsetScale < 0.5) {
      if (scale < 0.25) return 0.25;
    } else {
      if (percent < smallest) return this.percentToScale(smallest, offsetScale);
    }

    if (percent > biggest) return this.percentToScale(biggest, offsetScale);
    return scale;
  }

  public onWheel(event: WheelEvent, container: Rect, scale: number, offsetScale: number) {
    const metaKey = isMetaKey(event);
    const offsetChange = new Rect();

    if (isTrackpad(event) && !metaKey) {
      const change = event.deltaZ * scale;
      scale -= change;

      offsetChange.x -= (event.deltaX * 2) / scale;
      offsetChange.y -= (event.deltaY * 2) / scale;
    } else {
      let val = normalizeScrollEvent(event).pixelY;
      if (Math.abs(val) > 100) val /= 10;

      const change = (val / 100) * scale;
      const oldScale = scale;
      scale -= change;

      const mouseOffsetX = event.clientX - container.width / 2;
      const mouseOffsetY = event.clientY - container.height / 2;

      scale = this.clipScale(scale, offsetScale);

      if (change !== 0 && oldScale !== scale) {
        const scale2 = scale * oldScale;
        offsetChange.x += (mouseOffsetX * change) / scale2;
        offsetChange.y += (mouseOffsetY * change) / scale2;
      }
    }
    return { offsetChange, scale };
  }

  public touchMoveSingle(offsetScale: number, scale: number, touch: Rect, previousTouch: Rect) {
    const offsetChange = new Rect();
    const newTouch = [touch.x, touch.y];
    const diff = [newTouch[0] - previousTouch.x, newTouch[1] - previousTouch.y];
    offsetChange.y += diff[1] / scale;
    offsetChange.x += diff[0] / scale;
    const zoomedIn = scale > 1;
    return { zoomedIn, offsetChange, diff };
  }

  public touchMoveMultiple(event: TouchEvent, previousTouches: Rect[], scale: number, offsetScale: number) {
    const touches = Rect.fromTouchEvent(event);
    const dist = touches[0].subtract(touches[1]);
    const previousDist = previousTouches[0].subtract(previousTouches[1]);
    const mag = dist.magnitude();
    const previousMag = previousDist.magnitude();
    const zoom = previousMag - mag;

    scale -= (zoom / 250) * scale;

    const center = touches[0].subtract(dist.times(0.5));
    const previousCenter = previousTouches[0].subtract(previousDist.times(0.5));

    const moved = center.subtract(previousCenter);

    const offsetChange = new Rect();
    offsetChange.x += moved.x / scale;
    offsetChange.y += moved.y / scale;
    scale = this.clipScale(scale, offsetScale);

    return { offsetChange, scale };
  }
}
